import { useState, useEffect } from "react";
import { auditLogsApi, contractsApi, type AuditLog, type Contract } from "@/lib/api";

function downloadCSV(filename: string, headers: string[], rows: string[][]) {
  const bom = "﻿";
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const csv = bom + [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" });
}

const ACTION_TYPE_OPTIONS = [
  { value: "status_change", label: "Status Changed" },
  { value: "created", label: "Contract Created" },
  { value: "alert_acknowledged", label: "Alert Acknowledged" },
  { value: "alert_sent", label: "Alert Sent" },
  { value: "renewal_decided", label: "Renewal Decision" },
  { value: "note_added", label: "Note Added" },
];

interface FilterState {
  contractId: string;
  dateFrom: string;
  dateTo: string;
  actionType: string;
}

export default function AuditReport() {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [entries, setEntries] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewed, setPreviewed] = useState(false);
  const [filters, setFilters] = useState<FilterState>({ contractId: "", dateFrom: "", dateTo: "", actionType: "" });

  useEffect(() => {
    // Load a flat list of contracts for the dropdown
    contractsApi.list(1, {}).then((res) => setContracts(res.data.data)).catch(() => {});
  }, []);

  const updateFilter = (key: keyof FilterState, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const fetchEntries = async () => {
    setLoading(true);
    setPreviewed(true);
    try {
      const res = await auditLogsApi.list({
        contract_id: filters.contractId ? Number(filters.contractId) : undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        action_type: filters.actionType || undefined,
      });
      setEntries(res.data.data);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const selectedContract = contracts.find((c) => String(c.id) === filters.contractId);
    const filename = selectedContract
      ? `Audit_Trail_${selectedContract.contract_number.replace(/\//g, "-")}.csv`
      : `Audit_Trail_Report_${new Date().toISOString().slice(0, 10)}.csv`;
    const headers = ["Timestamp", "Contract #", "Action Type", "Action", "Performed By", "Notes"];
    const rows = entries.map((e) => {
      const c = contracts.find((c) => c.id === e.contract_id);
      return [
        formatDateTime(e.created_at),
        c?.contract_number ?? String(e.contract_id),
        e.action_type,
        e.action,
        e.user?.name ?? "System",
        e.notes ?? "",
      ];
    });
    downloadCSV(filename, headers, rows);
  };

  const selectedContract = contracts.find((c) => String(c.id) === filters.contractId);

  return (
    <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
          <i className="ri-history-line text-primary-500"></i>
        </div>
        <div>
          <h2 className="text-foreground-900 text-sm font-semibold">Audit Trail Report</h2>
          <p className="text-foreground-400 text-xs">Filter audit log entries by contract, date range, or action type</p>
        </div>
        {previewed && (
          <span className="ml-auto text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full whitespace-nowrap">
            {entries.length} entr{entries.length !== 1 ? "ies" : "y"}
          </span>
        )}
      </div>

      <div className="p-5">
        {/* Contract banner */}
        {selectedContract && (
          <div className="mb-4 p-3 bg-background-50 rounded-lg border border-background-200 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
            <span className="text-foreground-500">Contract: <strong className="text-foreground-800">{selectedContract.contract_number}</strong></span>
            <span className="text-foreground-500">Supplier: <strong className="text-foreground-700">{selectedContract.vendor_name}</strong></span>
            {selectedContract.contract_value && (
              <span className="text-foreground-500">Value: <strong className="text-foreground-700">R {parseFloat(selectedContract.contract_value).toLocaleString("en-ZA")}</strong></span>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="block text-foreground-500 text-[11px] font-medium mb-1.5">Contract</label>
            <select value={filters.contractId} onChange={(e) => updateFilter("contractId", e.target.value)}
              className="w-full h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white cursor-pointer focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200">
              <option value="">All Contracts</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>{c.contract_number} — {c.vendor_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-foreground-500 text-[11px] font-medium mb-1.5">Date From</label>
            <input type="date" value={filters.dateFrom} onChange={(e) => updateFilter("dateFrom", e.target.value)}
              className="w-full h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200" />
          </div>
          <div>
            <label className="block text-foreground-500 text-[11px] font-medium mb-1.5">Date To</label>
            <input type="date" value={filters.dateTo} onChange={(e) => updateFilter("dateTo", e.target.value)}
              className="w-full h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200" />
          </div>
          <div>
            <label className="block text-foreground-500 text-[11px] font-medium mb-1.5">Action Type</label>
            <select value={filters.actionType} onChange={(e) => updateFilter("actionType", e.target.value)}
              className="w-full h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white cursor-pointer focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200">
              <option value="">All Actions</option>
              {ACTION_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button onClick={fetchEntries} disabled={loading}
            className="h-9 px-4 bg-background-100 text-foreground-700 text-sm font-medium rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 disabled:opacity-60">
            {loading ? <i className="ri-loader-4-line animate-spin text-sm"></i> : <i className="ri-search-line text-sm"></i>}
            Preview Results
          </button>
          <button onClick={handleExport} disabled={entries.length === 0}
            className="h-9 px-4 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5">
            <i className="ri-download-2-line text-sm"></i>
            Export {entries.length > 0 ? `${entries.length} Entr${entries.length !== 1 ? "ies" : "y"}` : ""} (CSV)
          </button>
          {entries.length > 0 && (
            <span className="text-xs text-foreground-400 sm:ml-1">Suitable for audit committee review</span>
          )}
        </div>

        {/* Preview Table */}
        {previewed && (
          <div className="mt-4 border border-background-200 rounded-lg overflow-hidden">
            {loading ? (
              <div className="py-10 text-center text-foreground-400 text-sm">
                <i className="ri-loader-4-line animate-spin mr-2"></i>Loading...
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-50 border-b border-background-200">
                      {["Timestamp", "Contract #", "Action", "Performed By", "Notes"].map((h) => (
                        <th key={h} className={`text-left px-3 py-2.5 text-foreground-500 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${h === "Notes" ? "max-w-[280px]" : ""}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => {
                      const c = contracts.find((c) => c.id === e.contract_id);
                      const isSystem = !e.user;
                      return (
                        <tr key={e.id} className={`border-b border-background-100 hover:bg-background-50/50 transition-colors ${i === entries.length - 1 ? "border-b-0" : ""}`}>
                          <td className="px-3 py-2.5 text-foreground-700 text-xs whitespace-nowrap">{formatDateTime(e.created_at)}</td>
                          <td className="px-3 py-2.5 text-foreground-800 text-xs font-medium whitespace-nowrap">{c?.contract_number ?? `#${e.contract_id}`}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${isSystem ? "bg-background-100 text-foreground-500" : "bg-primary-50 text-primary-700"}`}>
                              {e.action}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-foreground-700 text-xs whitespace-nowrap">{e.user?.name ?? "System"}</td>
                          <td className="px-3 py-2.5 text-foreground-500 text-xs max-w-[280px] truncate">{e.notes ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {entries.length === 0 && (
                  <div className="py-10 text-center text-foreground-400 text-sm">No audit entries match the selected filters</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
